import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Crown, 
  Search,
  UserCheck,
  Mail,
  Phone,
  Users,
  Briefcase,
  Link,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const RMMasterPage = () => {
  const { token } = useAuth();
  const [rms, setRms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchRms = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/rms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRms(data);
      } else {
        toast.error('Failed to load RMs');
      }
    } catch (err) {
      toast.error('Error loading RMs');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRms();
  }, [fetchRms]);

  const filteredRms = rms.filter((rm) => {
    const matchesSearch =
      rm.rm_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rm.email && rm.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || rm.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: rms.length,
    active: rms.filter((rm) => rm.status === 'Active').length,
    totalDealers: rms.reduce((sum, rm) => sum + (rm.dealer_count || 0), 0),
    linked: rms.filter((rm) => rm.linked_user_id).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Crown className="w-7 h-7 text-purple-400" />
            RM Master
          </h1>
          <p className="text-slate-400 mt-1">Read-only RM summary. Use Staff Management to add or edit RMs.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Crown className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-slate-400">Total RMs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <UserCheck className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.active}</p>
                <p className="text-xs text-slate-400">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Briefcase className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.totalDealers}</p>
                <p className="text-xs text-slate-400">Total Dealers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Link className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.linked}</p>
                <p className="text-xs text-slate-400">Linked Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-dark">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by code, name, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 input-vault"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40 input-vault">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* RM Table - Read Only */}
      <Card className="card-dark">
        <CardHeader>
          <CardTitle className="text-white">RM Directory</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : filteredRms.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Crown className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No RMs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-300">Code</TableHead>
                    <TableHead className="text-slate-300">Name</TableHead>
                    <TableHead className="text-slate-300">Contact</TableHead>
                    <TableHead className="text-slate-300">Dealers</TableHead>
                    <TableHead className="text-slate-300">Clients</TableHead>
                    <TableHead className="text-slate-300">Status</TableHead>
                    <TableHead className="text-slate-300">User Link</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRms.map((rm) => (
                    <TableRow key={rm.rm_code} className="border-slate-700">
                      <TableCell className="text-white font-mono font-medium">{rm.rm_code}</TableCell>
                      <TableCell className="text-white">{rm.name}</TableCell>
                      <TableCell className="text-slate-300">
                        <div className="space-y-1">
                          {rm.email && (
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="w-3 h-3" /> {rm.email}
                            </div>
                          )}
                          {rm.mobile && (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="w-3 h-3" /> {rm.mobile}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-blue-400" />
                          <span className="text-white font-medium">{rm.dealer_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-green-400" />
                          <span className="text-white font-medium">{rm.client_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            rm.status === 'Active'
                              ? 'bg-green-500/20 text-green-400 border-green-500/30'
                              : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }
                        >
                          {rm.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {rm.linked_user_id ? (
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                            {rm.linked_user_email || 'Linked'}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                            Not Linked
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RMMasterPage;
